import _ from 'lodash'
import { isElectron } from 'src/plugins/helpers/electron'
import { getAppUrl } from 'src/plugins/mixins/classic.mixin'
import { setCookie } from 'src/plugins/helpers/functions'
import * as storage from 'src/plugins/helpers/storage'
import { isAdminImpersonating } from 'src/utils'
import { mapActions, mapState } from 'vuex'
import * as Roles from '../../constants/roles'
import goBackMixin from './goback.mixin'

export default _.merge({
  methods: {
    ...mapActions('auth', ['logout', 'stopImpersonation']),

    ...mapActions(['resetVuex', 'setDialerIsReady']),

    /*
        * Updated on 06/11/2018
        * Campaign Permissions:
        *
        * 'list campaign'
        * 'view campaign'
        * 'create campaign'
        * 'update campaign'
        * 'archive campaign'
        * 'restore campaign'
        * 'toggle active status campaign'
        * 'upload file campaign'
        * 'delete file campaign'

        * Communication Permissions:
        *
        * 'list communication'
        * 'view communication'
        * 'tag communication'
        * 'note communication'

        * Company Permissions:
        *
        * 'list company'
        * 'view company'
        * 'create company'
        * 'update company'
        * 'archive company'
        * 'restore company'
        * 'change current company'
        * 'change integration settings company'
        * 'see integration details company'

        * Contact Permissions:
        *
        * 'list contact'
        * 'view contact'
        * 'update contact'
        * 'archive contact'
        * 'restore contact'
        * 'toggle block contact'

        * Destination Number Permissions:
        *
        * 'list destination number'
        * 'view destination number'
        * 'create destination number'
        * 'update destination number'
        * 'archive destination number'
        * 'restore destination number'
        * 'toggle active destination number'

        * Filter Permissions:
        *
        * 'list filter'
        * 'view filter'
        * 'create filter'
        * 'update filter'
        * 'archive filter'
        * 'restore filter'

        * Report Permissions:
        *
        * 'list report'

        * Tag Permissions:
        *
        * 'list tag'
        * 'view tag'
        * 'create tag'
        * 'update tag'
        * 'delete tag'

        * User Permissions:
        *
        * 'list user'
        * 'view user'
        * 'create user'
        * 'update user'
        * 'archive user'
        * 'restore user'
        * 'toggle active user'

        * Telephony Permissions:
        *
        * 'send sms'
        * 'answer call'
        * 'make call'

        * Billing Permissions:
        *
        * 'add credit card'
        * 'change plan'
        * 'buy credits'
        * 'see usage page'
        * 'see chargebee portal'
         */
    hasPermissionTo (permissions, source = null) {
      // this has been added to provide us with a way to check permissions in beforeRouteEnter
      if (!source) {
        source = this.profile
      } else {
        source = _.get(source, 'profile', null)
      }

      // if user is logged out of the system when session expires
      if (!source) {
        return false
      }

      // if user doesn't have permissions
      if (!source.user_permissions) {
        return false
      }

      if (Array.isArray(permissions)) {
        const permission = { index: null }
        for (permission.index of permissions) {
          if (!source.user_permissions.includes(permission.index)) {
            return false
          }
        }
        return true
      } else {
        return source.user_permissions.includes(permissions)
      }
    },

    hasRole (roles, source = null) {
      // this has been added to provide us with a way to check roles in beforeRouteEnter
      if (!source) {
        source = this.profile
      } else {
        source = _.get(source, 'profile', null)
      }

      // if user is logged out of the system when session expires
      if (!source) {
        return false
      }

      // if user doesn't have roles
      if (!source.user_roles) {
        return false
      }

      if (Array.isArray(roles)) {
        const role = { data: null }
        for (role.data of roles) {
          if (source.user_roles.includes(role.data)) {
            return true
          }
        }
        return false
      } else {
        return source.user_roles.includes(roles)
      }
    },

    hideMenu () {
      if (this.$refs && this.$refs.menu) {
        this.$refs.menu.hide()
      }
    },

    logoutAction () {
      try {
        this.hideMenu()
        if (isElectron()) {
          this.setDialerIsReady(false)
        }
        if (this.isImpersonate) {
          // Capture before stopImpersonation() calls clear() and removes it
          const impersonationSource = storage.local.getItem('impersonation_source') || getAppUrl()
          const wasAdminImpersonation = isAdminImpersonating()
          const impersonatedCompanyId = storage.local.getItem('company_id')
          this.stopImpersonation()
            .then((response) => {
              this.resetVuex(['all'])
              if (wasAdminImpersonation) {
                // Admin impersonation: go straight to admin panel
                const adminPath = impersonatedCompanyId ? '/admin/company/' + impersonatedCompanyId + '?tab=company-user-list' : '/admin'
                window.location.href = impersonationSource + '?from_talk_2=1&logout=1&return=' + encodeURIComponent(adminPath)
              } else if (response.data.restored) {
                // Set shared auth cookie to restored user before redirecting
                if (response.data.hashed_token) {
                  setCookie('aloware_shared_auth_token', response.data.hashed_token, 30)
                  storage.local.setItem('shared_cookie', response.data.hashed_token)
                }
                // Regular user-to-user: redirect through api-core to clean its localStorage, then come back
                const returnUrl = encodeURIComponent(window.location.origin)
                window.location.href = impersonationSource + '?restore=1&token=' + encodeURIComponent(response.data.hashed_token) + '&return=' + returnUrl
              } else {
                window.location.href = impersonationSource + '?from_talk_2=1&logout=1&return=' + encodeURIComponent('/login')
              }
            })
        } else {
          this.logout()
            .then(() => {
              this.resetVuex(['all'])
              if (isElectron()) {
                window.location.reload()
              } else {
                // Redirect through api-core to clear its session too
                const appUrl = getAppUrl()
                window.location.href = appUrl + '?from_talk_2=1&logout=1&return=' + encodeURIComponent(window.location.origin)
              }
            })
        }
      } catch (err) {
        console.error(err)
      }
    }
  },
  computed: {
    ...mapState('auth', ['profile']),
    ...mapState('cache', ['currentCompany']),
    ...mapState(['usage']),

    hasReporterAccess () {
      return this.profile && this.profile.read_only_access
    },

    isAgent () {
      return this.hasRole(Roles.COMPANY_AGENT)
    },

    isAdmin () {
      return this.hasRole(Roles.COMPANY_ADMIN)
    },

    isBillingAdmin () {
      return this.hasRole(Roles.BILLING_ADMIN)
    },

    isSupervisor () {
      return this.hasRole(Roles.COMPANY_SUPERVISOR)
    },

    isAdminOrSupervisor () {
      return this.hasRole(Roles.COMPANY_ADMIN) || this.hasRole(Roles.COMPANY_SUPERVISOR)
    },

    isBillingAdminOrAdminOrSupervisor () {
      return this.isBillingAdmin || this.isAdminOrSupervisor
    },

    isAdminOrBillingAdmin () {
      return this.isAdmin || this.isBillingAdmin
    },

    /**
     * Decides if the Broadcast should be shown.
     *
     * @return {boolean}
     */
    shouldShowBroadcast () {
      if (this.currentCompany.bulk_sms_enabled || this.currentCompany.bulk_rvm_enabled) {
        return true
      }

      return false
    },

    /**
     * Decides if the Sequences should be shown.
     *
     * @return {boolean}
     */
    shouldShowSequences () {
      if (this.currentCompany.automation_enabled) {
        return true
      }

      return false
    },

    shouldSeeSequences () {
      if (!this.hasPermissionTo('view workflow')) {
        return false
      }

      if (!this.hasRole('Company Admin')) {
        return false
      }

      if (this.hasReporterAccess) {
        return false
      }

      return true
    },

    /**
     * Decides if the PowerDialer should be shown.
     *
     * @return {boolean}
     */
    shouldShowPowerDialer () {
      if (this.currentCompany.auto_dialer_enabled) {
        return true
      }

      return false
    },

    /**
     * Decides if the AloAi should be shown.
     *
     * @return {boolean}
     */
    shouldShowAloAi () {
      if (this.currentCompany.aloai_text_agents_enabled || this.currentCompany.aloai_voice_inbound_agents_enabled || this.currentCompany.aloai_voice_outbound_agents_enabled) {
        return true
      }

      return false
    },

    logoutLabel () {
      return this.isImpersonate ? 'Stop Impersonating' : 'Logout'
    },

    classicLogOutUrl () {
      const source = storage.local.getItem('impersonation_source') || getAppUrl()
      return source + '?from_talk_2=1&logout=1'
    },

    isBetaFeaturesEnabled () {
      const betaFeaturesCompanyIds = process.env.BETA_FEATURES_COMPANY_IDS

      if (process.env.APP_ENV !== 'production' || !betaFeaturesCompanyIds) {
        return true
      }

      return Object.values(betaFeaturesCompanyIds).includes(this.currentCompany.id)
    },

    canCreateLists () {
      return !!this.profile?.can_create_lists
    }
  }
}, goBackMixin)
